import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { sessionApi } from '@/services/api';
import { sanitizeSlug } from '@/utils/slug';

type SessionMeta = {
  freeTrialEnd: string | null;
  currentPlan: string | null;
  inFreeTrial: boolean;
  subscriptionAccess: 'allowed' | 'blocked';
};

type SessionSlugState = {
  slug: string | null;
  academyCode: string | null;
  academyName: string | null;
  role: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  meta: SessionMeta;
  refresh: () => Promise<string | null>;
  setMeta: (meta: Partial<SessionMeta>) => void;
};

const SessionSlugContext = createContext<SessionSlugState>({
  slug: null,
  academyCode: null,
  academyName: null,
  role: null,
  status: 'idle',
  meta: {
    freeTrialEnd: null,
    currentPlan: null,
    inFreeTrial: false,
    subscriptionAccess: 'allowed'
  },
  refresh: async () => null,
  setMeta: () => {}
});

export const SessionSlugProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [slug, setSlug] = useState<string | null>(null);
  const [academyCode, setAcademyCode] = useState<string | null>(null);
  const [academyName, setAcademyName] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [meta, setMetaState] = useState<SessionMeta>({
    freeTrialEnd: null,
    currentPlan: null,
    inFreeTrial: false,
    subscriptionAccess: 'allowed'
  });
  const inflightRef = useRef<Promise<string | null> | null>(null);

  const hasAnyToken = () => {
    if (typeof window === 'undefined') return false;
    return !!localStorage.getItem('academy_token') || !!localStorage.getItem('student_token');
  };

  const isAdminPath = () => {
    if (typeof window === 'undefined') return false;
    const path = window.location?.pathname || '';
    return path.startsWith('/admin');
  };

  const loadSlug = useCallback(async (): Promise<string | null> => {
    if (!hasAnyToken() || isAdminPath()) {
      setSlug(null);
      setAcademyCode(null);
      setStatus('ready');
      return null;
    }
    setStatus('loading');
    try {
      const res = await sessionApi.getSlug();
      const payload = res?.data || res;
      const nextSlug = sanitizeSlug(payload?.academyUrl || '');
      setSlug(nextSlug || null);
      setAcademyCode(payload?.academyCode || null);
      setAcademyName(payload?.academyName || null);
      const normalizedRole = payload?.role ? String(payload.role).toUpperCase() : null;
      setRole(normalizedRole);
      if (typeof window !== 'undefined') {
        if (normalizedRole) {
          localStorage.setItem('academy_role', normalizedRole);
        } else {
          localStorage.removeItem('academy_role');
        }
      }
      setStatus('ready');
      return nextSlug || null;
    } catch (err) {
      setSlug(null);
      setAcademyCode(null);
      setStatus('error');
      return null;
    }
  }, []);

  const refresh = useCallback(() => {
    if (!inflightRef.current) {
      inflightRef.current = (async () => {
        const result = await loadSlug();
        return result;
      })()
        .finally(() => {
          inflightRef.current = null;
        });
    }
    return inflightRef.current;
  }, [loadSlug]);

  const setMeta = useCallback((next: Partial<SessionMeta>) => {
    setMetaState((prev) => ({
      ...prev,
      ...next
    }));
  }, []);

  useEffect(() => {
    if (!isAdminPath()) {
      refresh().catch(() => {});
    } else {
      setStatus('ready');
    }
  }, [refresh]);

  return (
    <SessionSlugContext.Provider
      value={{
        slug,
        academyCode,
        academyName,
        role,
        status,
        meta,
        refresh,
        setMeta
      }}
    >
      {children}
    </SessionSlugContext.Provider>
  );
};

export const useSessionSlug = () => useContext(SessionSlugContext);


